import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { eur, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Loader2, ChefHat, X, Copy, FileText, Search } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "",
  category: "",
  ean: "",
  storage_conditions: "",
  usage_instructions: "",
  description: "",
  items: [],
  portions: 1,
  yield_weight: 0,
  portion_size: 0,
  shelf_life_days: 3,
  markup_percent: 300,
  vat_percent: 10,
  manual_allergens: [],
};

function computePreview(items, ingMap, recMap, portions, markup, vat, yieldWeight, portionSize) {
  let totalCost = 0,
    totalWeight = 0;
  const totals = { kcal: 0, protein: 0, carbs: 0, sugar: 0, fat: 0, saturated: 0, fiber: 0, salt: 0 };
  items.forEach((it) => {
    const qty = Number(it.quantity) || 0;
    const waste = Number(it.waste_percent) || 0;
    const usable = waste >= 100 ? 0.0001 : 1 - waste / 100;
    let unitCost = 0,
      unit = "g",
      nutr = {};
    if (it.item_type === "recipe") {
      const r = recMap[it.ingredient_id];
      if (!r) return;
      unitCost = r.total_weight ? r.total_cost / r.total_weight : 0;
      unit = "g";
      nutr = r.nutrition_per_100g || {};
    } else {
      const ing = ingMap[it.ingredient_id];
      if (!ing) return;
      unitCost = ing.unit_cost || 0;
      unit = ing.unit;
      nutr = ing.nutrition || {};
    }
    totalCost += (unitCost * qty) / usable;
    const f = qty / 100;
    Object.keys(totals).forEach((k) => (totals[k] += (nutr[k] || 0) * f));
    if (unit === "g" || unit === "ml") totalWeight += qty;
  });
  const effWeight = Number(yieldWeight) > 0 ? Number(yieldWeight) : totalWeight;
  const yieldLossPct = Number(yieldWeight) > 0 && totalWeight > 0 ? ((totalWeight - effWeight) / totalWeight) * 100 : 0;
  let p = portions || 1;
  if (Number(portionSize) > 0 && effWeight > 0) p = Math.max(1, Math.round(effWeight / Number(portionSize)));
  const costPerPortion = totalCost / p;
  const sellingNet = costPerPortion * (1 + (markup || 0) / 100);
  const sellingGross = sellingNet * (1 + (vat || 0) / 100);
  const fcPct = sellingNet ? (costPerPortion / sellingNet) * 100 : 0;
  const per100 = {};
  Object.keys(totals).forEach((k) => (per100[k] = effWeight ? (totals[k] / effWeight) * 100 : 0));
  const perPortion = {};
  Object.keys(totals).forEach((k) => (perPortion[k] = totals[k] / p));
  return { totalCost, totalWeight, effWeight, yieldLossPct, portions: p, costPerPortion, sellingNet, sellingGross, fcPct, per100, perPortion };
}

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [selIng, setSelIng] = useState("");
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const ingMap = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);
  const recMap = useMemo(() => Object.fromEntries(recipes.map((r) => [r.id, r])), [recipes]);

  const itemInfo = (it) => {
    if (it.item_type === "recipe") {
      const r = recMap[it.ingredient_id];
      if (!r) return null;
      return {
        name: `${r.name} (semilav.)`,
        unit: "g",
        unit_cost: r.total_weight ? r.total_cost / r.total_weight : 0,
      };
    }
    const ing = ingMap[it.ingredient_id];
    if (!ing) return null;
    return { name: ing.name, unit: ing.unit, unit_cost: ing.unit_cost || 0 };
  };

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/recipes"), api.get("/ingredients")])
      .then(([r, i]) => {
        setRecipes(r.data);
        setIngredients(i.data);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => {
    setForm(empty);
    setEditId(null);
    setSelIng("");
    setOpen(true);
  };

  const openEdit = (r) => {
    setForm({
      name: r.name,
      category: r.category || "",
      ean: r.ean || "",
      storage_conditions: r.storage_conditions || "",
      usage_instructions: r.usage_instructions || "",
      description: r.description || "",
      items: (r.items || []).map((i) => ({
        ingredient_id: i.ingredient_id,
        item_type: i.item_type || "ingredient",
        quantity: i.quantity,
        waste_percent: i.waste_percent || 0,
      })),
      portions: r.portions,
      yield_weight: r.yield_weight || 0,
      portion_size: r.portion_size || 0,
      shelf_life_days: r.shelf_life_days,
      markup_percent: r.markup_percent,
      vat_percent: r.vat_percent,
      manual_allergens: r.manual_allergens || [],
    });
    setEditId(r.id);
    setOpen(true);
  };

  const addItem = () => {
    if (!selIng) return;
    const [kind, id] = selIng.split(":");
    const item_type = kind === "rec" ? "recipe" : "ingredient";
    if (form.items.find((i) => i.ingredient_id === id)) {
      toast.error("Elemento già presente");
      return;
    }
    const defWaste = item_type === "ingredient" ? ingMap[id]?.default_waste || 0 : 0;
    setForm((f) => ({ ...f, items: [...f.items, { ingredient_id: id, item_type, quantity: 100, waste_percent: defWaste }] }));
    setSelIng("");
  };

  const updateQty = (id, q) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((i) => (i.ingredient_id === id ? { ...i, quantity: q } : i)),
    }));

  const updateWaste = (id, w) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((i) => (i.ingredient_id === id ? { ...i, waste_percent: w } : i)),
    }));

  const removeItem = (id) =>
    setForm((f) => ({ ...f, items: f.items.filter((i) => i.ingredient_id !== id) }));

  const preview = useMemo(
    () =>
      computePreview(
        form.items,
        ingMap,
        recMap,
        Number(form.portions),
        Number(form.markup_percent),
        Number(form.vat_percent),
        Number(form.yield_weight),
        Number(form.portion_size)
      ),
    [form, ingMap, recMap]
  );

  useEffect(() => {
    if (Number(form.portion_size) > 0) {
      setForm((f) => (f.portions === preview.portions ? f : { ...f, portions: preview.portions }));
    }
  }, [preview.portions, form.portion_size]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nome ricetta obbligatorio");
    if (form.items.length === 0) return toast.error("Aggiungi almeno un ingrediente");
    setSaving(true);
    const payload = {
      ...form,
      portions: Number(form.portions) || 1,
      yield_weight: Number(form.yield_weight) || 0,
      portion_size: Number(form.portion_size) || 0,
      shelf_life_days: Number(form.shelf_life_days) || 0,
      markup_percent: Number(form.markup_percent) || 0,
      vat_percent: Number(form.vat_percent) || 0,
      items: form.items.map((i) => ({
        ingredient_id: i.ingredient_id,
        item_type: i.item_type || "ingredient",
        quantity: Number(i.quantity) || 0,
        waste_percent: Number(i.waste_percent) || 0,
      })),
    };
    try {
      if (editId) await api.put(`/recipes/${editId}`, payload);
      else await api.post("/recipes", payload);
      toast.success(editId ? "Ricetta aggiornata" : "Ricetta creata");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa ricetta?")) return;
    await api.delete(`/recipes/${id}`);
    toast.success("Ricetta eliminata");
    load();
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/recipes/${id}/duplicate`);
      toast.success("Ricetta duplicata");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  };

  return (
    <div className="space-y-6" data-testid="recipes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Ricette</h1>
          <p className="text-muted-foreground mt-1">Componi i piatti e calcola costi e valori nutrizionali</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate("/menu")} className="gap-2" data-testid="menu-export-btn">
            <FileText className="w-4 h-4" /> Listino / Menù
          </Button>
          <Button onClick={openNew} className="rounded-full gap-2" data-testid="add-recipe-btn">
            <Plus className="w-4 h-4" /> Nuova ricetta
          </Button>
        </div>
      </div>

      {recipes.length > 0 && (
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca ricetta per nome..."
            className="pl-9"
            data-testid="recipe-search-input"
          />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : recipes.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <ChefHat className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground mb-4">Nessuna ricetta. Crea il tuo primo prodotto.</p>
          <Button onClick={openNew} className="rounded-full" data-testid="empty-add-recipe">
            <Plus className="w-4 h-4 mr-2" /> Crea ricetta
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {recipes
            .filter((r) => r.name.toLowerCase().includes(query.trim().toLowerCase()))
            .map((r) => (
            <Card key={r.id} className="p-6 border-border" data-testid={`recipe-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-xl font-bold tracking-tight">{r.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {r.category ? `${r.category} · ` : ""}{r.portions} porzioni · {r.items.length} ingredienti · {r.shelf_life_days} gg
                  </p>
                </div>
                <span
                  className={`text-sm font-mono px-2.5 py-1 rounded-full ${
                    r.food_cost_pct <= 35 ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"
                  }`}
                >
                  FC {r.food_cost_pct}%
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-border">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Costo tot.</p>
                  <p className="font-mono font-semibold mt-0.5">{eur(r.total_cost)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Costo/porz.</p>
                  <p className="font-mono font-semibold mt-0.5">{eur(r.cost_per_portion)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Vendita (IVA)</p>
                  <p className="font-mono font-semibold mt-0.5 text-primary">{eur(r.selling_price_gross)}</p>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                <div className="bg-secondary/60 rounded-md py-2">
                  <p className="font-mono text-sm font-semibold">{r.nutrition_per_portion?.kcal}</p>
                  <p className="text-[10px] text-muted-foreground">kcal/porz</p>
                </div>
                <div className="bg-secondary/60 rounded-md py-2">
                  <p className="font-mono text-sm font-semibold">{r.nutrition_per_portion?.protein}g</p>
                  <p className="text-[10px] text-muted-foreground">proteine</p>
                </div>
                <div className="bg-secondary/60 rounded-md py-2">
                  <p className="font-mono text-sm font-semibold">{r.nutrition_per_portion?.carbs}g</p>
                  <p className="text-[10px] text-muted-foreground">carboidr.</p>
                </div>
                <div className="bg-secondary/60 rounded-md py-2">
                  <p className="font-mono text-sm font-semibold">{r.nutrition_per_portion?.fat}g</p>
                  <p className="text-[10px] text-muted-foreground">grassi</p>
                </div>
              </div>

              {r.allergens?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-4">
                  {r.allergens.map((a) => (
                    <Badge key={a} variant="secondary" className="text-[10px]">
                      {a}
                    </Badge>
                  ))}
                </div>
              )}

              <div className="flex gap-2 mt-5">
                <Button variant="secondary" size="sm" className="flex-1 gap-2" onClick={() => openEdit(r)} data-testid={`edit-recipe-${r.id}`}>
                  <Pencil className="w-4 h-4" /> Modifica
                </Button>
                <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate(`/scheda/${r.id}`)} data-testid={`sheet-recipe-${r.id}`}>
                  <FileText className="w-4 h-4" /> Scheda
                </Button>
                <Button variant="ghost" size="icon" onClick={() => duplicate(r.id)} data-testid={`duplicate-recipe-${r.id}`}>
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(r.id)} data-testid={`delete-recipe-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Modifica ricetta" : "Nuova ricetta"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 py-2">
            {/* left: builder */}
            <div className="lg:col-span-3 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Nome ricetta</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Lasagna al ragù" data-testid="recipe-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Categoria / Reparto</Label>
                  <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Es. Primi" data-testid="recipe-category-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Codice EAN (per codice a barre in etichetta)</Label>
                <Input value={form.ean} onChange={(e) => setForm({ ...form, ean: e.target.value })} placeholder="Es. 8001234567890" data-testid="recipe-ean-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Conservazione (etichetta)</Label>
                  <Input value={form.storage_conditions} onChange={(e) => setForm({ ...form, storage_conditions: e.target.value })} placeholder="Vuoto = predefinita" data-testid="recipe-storage-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Modalità d'uso (etichetta)</Label>
                  <Input value={form.usage_instructions} onChange={(e) => setForm({ ...form, usage_instructions: e.target.value })} placeholder="Vuoto = predefinita" data-testid="recipe-usage-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Descrizione / procedimento</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="recipe-desc-input" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Peso prodotto finito (g)</Label>
                  <Input
                    type="number"
                    value={form.yield_weight}
                    onChange={(e) => setForm({ ...form, yield_weight: e.target.value })}
                    placeholder="0 = crudo"
                    data-testid="recipe-yield-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Peso porzione (g)</Label>
                  <Input
                    type="number"
                    value={form.portion_size}
                    onChange={(e) => setForm({ ...form, portion_size: e.target.value })}
                    placeholder="0 = manuale"
                    data-testid="recipe-portionsize-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Porzioni {Number(form.portion_size) > 0 && <span className="text-[10px] text-primary">(auto)</span>}</Label>
                  <Input
                    type="number"
                    value={Number(form.portion_size) > 0 ? preview.portions : form.portions}
                    onChange={(e) => setForm({ ...form, portions: e.target.value })}
                    disabled={Number(form.portion_size) > 0}
                    data-testid="recipe-portions-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Conservazione (gg)</Label>
                  <Input type="number" value={form.shelf_life_days} onChange={(e) => setForm({ ...form, shelf_life_days: e.target.value })} data-testid="recipe-shelf-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Ricarico (%)</Label>
                  <Input type="number" value={form.markup_percent} onChange={(e) => setForm({ ...form, markup_percent: e.target.value })} data-testid="recipe-markup-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>IVA %</Label>
                  <Input type="number" value={form.vat_percent} onChange={(e) => setForm({ ...form, vat_percent: e.target.value })} data-testid="recipe-vat-input" />
                </div>
              </div>

              <div className="pt-2">
                <Label>Aggiungi ingrediente</Label>
                <div className="flex gap-2 mt-1.5">
                  <Select value={selIng} onValueChange={setSelIng}>
                    <SelectTrigger data-testid="recipe-ingredient-select">
                      <SelectValue placeholder="Seleziona ingrediente o semilavorato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Ingredienti</SelectLabel>
                        {ingredients.map((i) => (
                          <SelectItem key={i.id} value={`ing:${i.id}`}>
                            {i.name} ({i.unit === "pz" ? `${eur(i.unit_cost)}/pz` : `${eur(i.unit_cost * 1000)}/${i.unit === "ml" ? "L" : "kg"}`})
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      {recipes.filter((r) => r.id !== editId).length > 0 && (
                        <SelectGroup>
                          <SelectLabel>Semilavorati (ricette)</SelectLabel>
                          {recipes
                            .filter((r) => r.id !== editId)
                            .map((r) => (
                              <SelectItem key={r.id} value={`rec:${r.id}`}>
                                {r.name}
                              </SelectItem>
                            ))}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                  <Button type="button" onClick={addItem} className="shrink-0" data-testid="recipe-add-item-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {ingredients.length === 0 && (
                  <p className="text-xs text-destructive mt-1">Aggiungi prima degli ingredienti.</p>
                )}
              </div>

              <div className="space-y-2">
                {form.items.length > 0 && (
                  <div className="flex items-center gap-2 px-2.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <span className="flex-1">Componente</span>
                    <span className="w-24 text-center">Q.tà netta</span>
                    <span className="w-6"></span>
                    <span className="w-16 text-center">Scarto%</span>
                    <span className="w-20 text-right">Costo</span>
                    <span className="w-7"></span>
                  </div>
                )}
                {form.items.map((it) => {
                  const info = itemInfo(it);
                  if (!info) return null;
                  const waste = Number(it.waste_percent) || 0;
                  const usable = waste >= 100 ? 0.0001 : 1 - waste / 100;
                  const lineCost = (info.unit_cost * (Number(it.quantity) || 0)) / usable;
                  return (
                    <div key={it.ingredient_id} className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2.5" data-testid={`recipe-item-${it.ingredient_id}`}>
                      <span className="flex-1 text-sm font-medium truncate">{info.name}</span>
                      <Input
                        type="number"
                        value={it.quantity}
                        onChange={(e) => updateQty(it.ingredient_id, e.target.value)}
                        className="w-24 h-8"
                        data-testid={`recipe-qty-${it.ingredient_id}`}
                      />
                      <span className="text-xs text-muted-foreground w-6">{info.unit}</span>
                      <Input
                        type="number"
                        value={it.waste_percent ?? 0}
                        onChange={(e) => updateWaste(it.ingredient_id, e.target.value)}
                        className="w-16 h-8"
                        data-testid={`recipe-waste-${it.ingredient_id}`}
                      />
                      <span className="font-mono text-sm w-20 text-right">{eur(lineCost)}</span>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeItem(it.ingredient_id)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* right: live preview */}
            <div className="lg:col-span-2">
              <div className="bg-primary text-primary-foreground rounded-xl p-5 sticky top-0" data-testid="recipe-preview">
                <p className="text-xs uppercase tracking-[0.2em] opacity-70">Anteprima calcolo</p>
                <div className="mt-4 space-y-3">
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm opacity-80">Peso finale</span>
                    <span className="font-mono font-semibold">{Math.round(preview.effWeight)} g</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm opacity-80">Porzioni</span>
                    <span className="font-mono font-semibold">{preview.portions}</span>
                  </div>
                  {Number(form.yield_weight) > 0 && (
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm opacity-80">Calo in cottura</span>
                      <span className="font-mono font-semibold text-accent" data-testid="cooking-loss">
                        {preview.yieldLossPct >= 0 ? "-" : "+"}
                        {Math.abs(preview.yieldLossPct).toFixed(1)}%
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm opacity-80">Costo totale</span>
                    <span className="font-mono font-bold text-lg">{eur(preview.totalCost)}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm opacity-80">Costo / porzione</span>
                    <span className="font-mono font-semibold">{eur(preview.costPerPortion)}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm opacity-80">Prezzo netto</span>
                    <span className="font-mono font-semibold">{eur(preview.sellingNet)}</span>
                  </div>
                  <div className="flex justify-between items-baseline border-t border-white/20 pt-3">
                    <span className="text-sm opacity-80">Prezzo con IVA</span>
                    <span className="font-mono font-bold text-xl text-accent">{eur(preview.sellingGross)}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm opacity-80">Food cost</span>
                    <span className="font-mono font-semibold">{preview.fcPct.toFixed(1)}%</span>
                  </div>
                </div>

                <div className="mt-5 pt-4 border-t border-white/20">
                  <p className="text-xs uppercase tracking-wider opacity-70 mb-2">Per porzione</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between"><span className="opacity-70">Energia</span><span className="font-mono">{Math.round(preview.perPortion.kcal)} kcal</span></div>
                    <div className="flex justify-between"><span className="opacity-70">Proteine</span><span className="font-mono">{preview.perPortion.protein.toFixed(1)}g</span></div>
                    <div className="flex justify-between"><span className="opacity-70">Carboidr.</span><span className="font-mono">{preview.perPortion.carbs.toFixed(1)}g</span></div>
                    <div className="flex justify-between"><span className="opacity-70">Grassi</span><span className="font-mono">{preview.perPortion.fat.toFixed(1)}g</span></div>
                  </div>
                  <p className="text-xs uppercase tracking-wider opacity-70 mt-4 mb-2">Per 100 g</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between"><span className="opacity-70">Energia</span><span className="font-mono">{Math.round(preview.per100.kcal)} kcal</span></div>
                    <div className="flex justify-between"><span className="opacity-70">Proteine</span><span className="font-mono">{preview.per100.protein.toFixed(1)}g</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button onClick={save} disabled={saving} className="rounded-full" data-testid="save-recipe-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva ricetta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
